import { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, TrendingDown, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { getCurrentUser } from '../utils/sessionManager';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShortfallHistory = () => {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterItem, setFilterItem] = useState('all');
  const [filterReason, setFilterReason] = useState('all');
  const [userVendorId, setUserVendorId] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    // Get logged-in user's vendor ID
    const initializeUser = async () => {
      try {
        const user = getCurrentUser();
        if (user) {
          if (user.vendor_id) {
            setUserVendorId(user.vendor_id);
            setFilterVendor(user.vendor_id); // Auto-filter to user's vendor
          } else {
            // User doesn't have vendor_id, fetch all data
            fetchMasterData();
            fetchLedger();
          }
        } else {
          // No user data, fetch all
          fetchMasterData();
          fetchLedger();
        }
      } catch (error) {
        console.error('Error getting user vendor ID:', error);
        fetchMasterData();
        fetchLedger();
      }
    };
    
    initializeUser();
  }, []);

  useEffect(() => {
    // Only fetch when userVendorId is set (not null)
    if (userVendorId) {
      fetchMasterData();
      fetchLedger();
    }
  }, [userVendorId]);

  useEffect(() => {
    fetchLedger();
  }, [pagination.page, filterVendor, filterItem]);

  const fetchMasterData = async () => {
    try {
      const [vendorsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/items`)
      ]);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchLedger = async () => {
    setLoading(true);
    try {
      // Build API URL with filters and pagination
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: 'created_ts',
        sort_order: 'desc'
      });
      
      // Send vendor filter to backend (use filterVendor for non-vendor users, userVendorId for vendor users)
      const vendorParam = userVendorId || (filterVendor && filterVendor !== 'all' ? filterVendor : null);
      if (vendorParam) {
        params.append('vendor_id', vendorParam);
      }
      
      // Send item filter to backend
      if (filterItem && filterItem !== 'all') {
        params.append('item_id', filterItem);
      }
      
      const response = await axios.get(`${API}/vendor-ledger/all-pos?${params.toString()}`);
      
      if (response.data.data) {
        setLedgerEntries(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setLedgerEntries(response.data);
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || id;
  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'SHIPMENT_SHORTAGE': return 'bg-orange-100 text-orange-800';
      case 'QUALITY_REJECTION': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEntries = ledgerEntries.filter(entry => {
    if (filterReason && filterReason !== 'all' && entry.reason !== filterReason) return false;
    return true;
  });

  // Calculate summary statistics
  const totalVariance = filteredEntries.reduce((sum, entry) => 
    sum + (entry.shipment_variance || 0) + (entry.quality_rejected || 0), 0);
  
  const entriesWithIssues = filteredEntries.filter(e => e.reason);
  const avgVariancePercent = entriesWithIssues.length > 0 
    ? entriesWithIssues.reduce((sum, entry) => {
        const variance = (entry.shipment_variance || 0) + (entry.quality_rejected || 0);
        return sum + ((variance / entry.expected_qty) * 100);
      }, 0) / entriesWithIssues.length 
    : 0;

  const totalQualityRejected = filteredEntries.reduce((sum, entry) => 
    sum + (entry.quality_rejected || 0), 0);
  const totalRework = filteredEntries.reduce((sum, entry) => 
    sum + (entry.rework_qty || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total MRO Entries</p>
                <p className="text-2xl font-bold">{filteredEntries.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Variance Quantity</p>
                <p className="text-2xl font-bold">{Math.round(totalVariance)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Variance %</p>
                <p className="text-2xl font-bold">{avgVariancePercent.toFixed(1)}%</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Quality Issues</p>
                <p className="text-lg font-bold text-purple-600">
                  Rejected: {Math.round(totalQualityRejected)}
                </p>
                <p className="text-xs text-slate-500">
                  Rework: {Math.round(totalRework)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shortfall Logs Table with Quality Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Vendor Ledger
          </CardTitle>
          <CardDescription>
            Track all vendor performance, quality rejections, and reallocations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Vendor</label>
              <Select value={filterVendor} onValueChange={(v) => { setFilterVendor(v); setPagination(prev => ({ ...prev, page: 1 })); }} disabled={!!userVendorId}>
                <SelectTrigger>
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {userVendorId && (
                <p className="text-xs text-slate-500 mt-1">Showing only your MROs</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
              <SearchableItemSelect
                items={items}
                value={filterItem}
                onValueChange={(v) => { setFilterItem(v); setPagination(prev => ({ ...prev, page: 1 })); }}
                placeholder="All items"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Reason</label>
              <Select value={filterReason} onValueChange={setFilterReason}>
                <SelectTrigger>
                  <SelectValue placeholder="All reasons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reasons</SelectItem>
                  <SelectItem value="SHIPMENT_SHORTAGE">Shipment Shortage</SelectItem>
                  <SelectItem value="GRN_SHORTAGE">GRN Shortage</SelectItem>
                  <SelectItem value="QUALITY_REJECTION">Quality Rejection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No purchase orders found
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>MRO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Ready for Pickup</TableHead>
                    <TableHead className="text-right">In Transit</TableHead>
                    <TableHead className="text-right bg-orange-50">
                      <div className="font-semibold text-orange-900">
                        Suspicious Qty
                        <div className="text-xs font-normal text-orange-700">(Pickup - Transit)</div>
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Store Received</TableHead>
                    <TableHead className="text-right">GRN</TableHead>
                    <TableHead className="text-right">Shipment Variance</TableHead>
                    <TableHead className="text-right">Quality Rejected</TableHead>
                    <TableHead className="text-right">Rework</TableHead>
                    <TableHead className="text-right">Final Qty</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry, idx) => {
                    const dateStr = new Date(entry.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                    
                    // Calculate suspicious quantity (Ready for Pickup - In Transit)
                    // If vendor committed but hasn't shipped, that's suspicious
                    const suspiciousQty = (entry.ready_for_pickup_qty || 0) - (entry.in_transit_qty || 0);
                    
                    return (
                    <TableRow key={`${entry.po_id}-${idx}`}>
                      <TableCell>{dateStr}</TableCell>
                      <TableCell className="font-medium">{entry.po_number}</TableCell>
                      <TableCell className="font-medium">{getVendorName(entry.vendor_id)}</TableCell>
                      <TableCell className="text-sm">{getItemInfo(entry.item_id)}</TableCell>
                      <TableCell className="text-right font-semibold">{Math.round(entry.expected_qty)}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-blue-600 font-semibold">{Math.round(entry.ready_for_pickup_qty)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-indigo-600 font-semibold">{Math.round(entry.in_transit_qty)}</span>
                      </TableCell>
                      <TableCell className={`text-right ${suspiciousQty > 0 ? 'bg-orange-400' : ''}`}>
                        <span className={`font-bold ${suspiciousQty > 0 ? 'text-red-700' : 'text-slate-600'}`}>
                          {Math.round(suspiciousQty)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-green-600 font-semibold">{Math.round(entry.store_received_qty)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-teal-600 font-semibold">{Math.round(entry.grn_qty)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-semibold ${entry.shipment_variance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {Math.round(entry.shipment_variance)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-red-600 font-semibold">{Math.round(entry.quality_rejected)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-orange-600 font-semibold">{Math.round(entry.rework_qty)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-purple-600 font-bold">{Math.round(entry.final_qty)}</span>
                      </TableCell>
                      <TableCell>
                        {entry.reason ? (
                          <Badge className={getReasonColor(entry.reason)}>
                            {entry.reason.replace(/_/g, ' ')}
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">OK</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShortfallHistory;
